import Phaser from 'phaser';
import { ALL_MUSIC_KEYS, musicPath } from '@/data/musicManifest';
import { ALL_SFX_MANIFEST, type SfxKey } from '@/systems/SfxKeys';
import { AudioBus, getAudioBus, type HowlLike, type AudioBusOptions } from '@/systems/AudioBus';

/**
 * AudioManager — Phaser-side adapter for combat + UI sound effects.
 *
 * Split: SfxKeys.ts owns the pure manifest (no Phaser). AudioBus.ts owns the
 * pure channel-state (volumes, mute, duck). This module wires both together
 * via Phaser's WebAudioSoundManager and the MusicDirectorAdapter's HowlLike
 * bridge for music.
 *
 * Usage:
 *   // In scene preload():
 *   this.audio = new AudioManager(this);
 *   this.audio.preload();
 *
 *   // In scene create() / game events:
 *   this.audio.play('sfx_punch_light');
 *   this.audio.applyOptions(saveSlot.options);
 *   this.audio.playMusic(new NoopHowl()); // swap NoopHowl for real Howl later
 */
export class AudioManager {
  private readonly scene: Phaser.Scene;
  private readonly bus: AudioBus;
  private currentMusic: HowlLike | null = null;

  constructor(scene: Phaser.Scene, bus?: AudioBus) {
    this.scene = scene;
    this.bus = bus ?? getAudioBus();
  }

  /** Expose the bus so callers can adjust volumes without a full options object. */
  getBus(): AudioBus {
    return this.bus;
  }

  /**
   * Queue every SFX and music track for loading. Call from the scene's preload() hook.
   * Missing files log a Phaser warning and are skipped gracefully.
   */
  preload(): void {
    for (const entry of ALL_SFX_MANIFEST) {
      this.scene.load.audio(entry.key, entry.path);
    }
    for (const key of ALL_MUSIC_KEYS) {
      this.scene.load.audio(key, musicPath(key));
    }
  }

  /**
   * Play a registered SFX at the bus's current SFX volume.
   * No-ops silently if the audio cache lacks the key.
   */
  play(key: SfxKey, config?: Phaser.Types.Sound.SoundConfig): void {
    if (!this.scene.cache.audio.has(key)) return;
    const busVolume = this.bus.getEffectiveVolume('sfx');
    this.scene.sound.play(key, { volume: busVolume, ...config });
  }

  /**
   * Start a music track via a HowlLike handle. Stops any current track first.
   * Volume is set from the bus immediately; call applyOptions() to update it
   * later when the player changes settings.
   */
  playMusic(howl: HowlLike): void {
    this.stopMusic();
    this.currentMusic = howl;
    howl.loop(true);
    howl.volume(this.bus.getEffectiveVolume('music'));
    howl.play();
  }

  /** Stop the current music track and release the handle. */
  stopMusic(): void {
    if (this.currentMusic !== null) {
      this.currentMusic.stop();
      this.currentMusic = null;
    }
  }

  /**
   * Temporarily duck music volume (call while a critical SFX plays).
   * The bus state is updated; call stopDuck() to restore.
   */
  startDuck(): void {
    this.bus.startDuck();
    this._applyMusicVolume();
  }

  stopDuck(): void {
    this.bus.stopDuck();
    this._applyMusicVolume();
  }

  /**
   * Load volume settings from a saved GameOptions object.
   * Propagates new volumes to the currently playing music track, if any.
   */
  applyOptions(opts: AudioBusOptions): void {
    this.bus.fromOptions(opts);
    this._applyMusicVolume();
  }

  /** Stop all currently playing sounds (e.g. on pause or scene transition). */
  stopAll(): void {
    this.stopMusic();
    this.scene.sound.stopAll();
  }

  private _applyMusicVolume(): void {
    if (this.currentMusic !== null) {
      this.currentMusic.volume(this.bus.getEffectiveVolume('music'));
    }
  }
}
